import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';

const RevisionModal = () => {
  const {
    showRevisionModal,
    setShowRevisionModal,
    revisionProject,
    revisionData,
    setProjects,
    setShowRevisionPaymentModal,
    setRevisionProject,
    showNotification,
    typeNames
  } = useAppContext();

  const [feedback, setFeedback] = useState(revisionData?.feedback || '');

  if (!showRevisionModal || !revisionProject || !revisionData) return null;

  // SAFETY CHECK: Stage 1 projects should never have revisions
  if (revisionProject?.stage === 1) {
    console.error('Stage 1 projects should not have revisions');
    setShowRevisionModal(false);
    showNotification('Error', 'Revisions are not available for Stage 1 projects', 'error');
    return null;
  }

  const isAdditionalRevision = revisionData.revisionNumber > revisionProject.revisions.maxIncludedRevisions;
  const revisionFee = isAdditionalRevision ? revisionProject.revisions.additionalRevisionFee : 0;

  const handleSubmitRevision = () => {
    if (!feedback.trim()) {
      showNotification("Feedback Required", "Please provide feedback for the revision", "warning");
      return;
    }

    if (isAdditionalRevision) {
      // For additional revisions, show payment modal
      setShowRevisionModal(false);
      setShowRevisionPaymentModal(true);
    } else {
      // For included revisions, process immediately
      setProjects(prev => prev.map(p => {
        if (p.id === revisionProject.id) {
          return {
            ...p,
            status: `revision-${revisionData.revisionNumber}-requested`,
            revisions: {
              ...p.revisions,
              currentRound: revisionData.revisionNumber,
              totalRevisionsUsed: p.revisions.totalRevisionsUsed + 1
            },
            communications: [
              ...p.communications,
              {
                type: "client",
                sender: revisionProject.client,
                message: `Revision ${revisionData.revisionNumber} requested: ${feedback}`,
                timestamp: new Date().toISOString().slice(0, 16).replace('T', ' ')
              },
              {
                type: "system",
                sender: "planAIdesign System",
                message: `Revision ${revisionData.revisionNumber} request received. We'll begin work on your revisions and have them ready within ${
                  revisionProject.stage === 2 ? 
                    (revisionProject.revisions.revisionTimeline || 3) + ' business days' :
                    '3-5 business days'
                }.`,
                timestamp: new Date(Date.now() + 1000).toISOString().slice(0, 16).replace('T', ' ')
              }
            ]
          };
        }
        return p;
      }));

      showNotification("Revision Requested", `Revision ${revisionData.revisionNumber} has been submitted to planAIdesign`, "success");
      setShowRevisionModal(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-xl max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            Request Revision {revisionData.revisionNumber}
          </h3>
          <button 
            onClick={() => setShowRevisionModal(false)} 
            className="text-2xl text-slate-400 hover:text-slate-600"
          >✕</button>
        </div>

        <div className="mb-6">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h4 className="font-semibold text-lg mb-2">{revisionProject.name}</h4>
            <p className="text-sm text-gray-600">
              {typeNames[revisionProject.type]} | Stage {revisionProject.stage}
            </p>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-sm">
                <strong>Revision Number:</strong> {revisionData.revisionNumber}
              </p>
              <p className="text-sm">
                <strong>Included Revisions Used:</strong> {revisionProject.revisions.totalRevisionsUsed}/{revisionProject.revisions.maxIncludedRevisions}
              </p>
              {isAdditionalRevision && (
                <p className="text-sm text-orange-600 font-semibold mt-2">
                  Additional Revision Fee: ${revisionFee}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block font-semibold mb-2">Revision Feedback *</label>
            <textarea
              rows={6}
              placeholder={revisionProject.stage === 2 ? 
                "Please describe the changes you'd like to the design..." :
                "Please describe the changes you'd like to the documentation..."
              }
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>

          {isAdditionalRevision && (
            <div className="mt-4 bg-orange-50 p-3 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>⚠️ Additional Revision:</strong> This revision exceeds your included revisions. 
                A fee of ${revisionFee} will be required before work can begin.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button 
            onClick={() => setShowRevisionModal(false)}
            className="flex-1 bg-gray-500 text-white py-3 rounded-lg font-semibold hover:bg-gray-600"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmitRevision}
            className="flex-1 bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700"
          >
            {isAdditionalRevision ? `Continue to Payment ($${revisionFee})` : 'Submit Revision Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionModal;